import { Storage } from "./Storage";
import { PIO } from "./role/pio";
import { IT } from "./role/it";
import { SOC } from "./role/soc";
import { SCADA } from "./role/scada";
import { AttackManager } from "./AttackManager";
import { RedHerringManager } from "./RedHerringManager";
import { NotificationManager } from "./NotificationManager";
import { StatusReport } from "./StatusReport";
import { SessionConfig } from "./SessionConfig";
import * as CONSTANTS from "./constants";
import { InvalidUsage } from "../exceptions/InvalidUsage";
import { ActionLogger } from "./ActionLogger";
import { loadNetwork } from "./NetworkLoader";
import axios from "axios";
import { Node } from "./Node";

/**
 * Holds data for the whole Session
 * Primitives are held here and references to more complex objects are held here.
 * Acts as a Facade for a much more complex system of classes
 */
export class Session {
    /** Holds session configuration data such as the seed or drill boolean */
    sessionConfig;
    round;
    roundStartTimeStamp;
    notifyImpact;
    totalInfectedCost;
    totalShutoffCost;
    storage;
    /** Worst case scenario is not necessarily the same thing that would happen to the player, but is a potential case that involved no player action */
    storageWorstCase;
    started;
    roles;
    attackManager;
    /** Worst case scenario is not necessarily the same thing that would happen to the player, but is a potential case that involved no player action */
    attackManagerWorstCase;
    notificationManager;
    numNodesCaught = 0;
    shutoffNodes = [];
    disconnectedEdges = [];
    redHerringManager;
    inBreak = false;

    /** Empty constructor allows import function to work. New Sessions should be built using Session.build()
     * @return {Session} session
     */
    constructor() {}

    /**
     * Factory function for building new Sessions
     * @param {SessionConfig} config
     * @return a new session with the config
     */
    static async build(config) {
        let instance = new Session();

        instance.sessionConfig = config;
        instance.round = 1;
        instance.roundStartTimeStamp = instance.getTimestampStart();
        instance.notifyImpact = 0;
        instance.totalInfectedCost = 0;
        instance.totalShutoffCost = 0;
        instance.storage = Storage.build();
        await loadNetwork(instance.storage);
        instance.storageWorstCase = Storage.jsonDeserialize(JSON.parse(JSON.stringify(instance.storage)));
        instance.storage.calculateCosts();
        instance.started = false;
        instance.roles = {
            [CONSTANTS.PIO]: new PIO(),
            [CONSTANTS.IT]: new IT(),
            [CONSTANTS.SOC]: new SOC(),
            [CONSTANTS.SCADA]: new SCADA()
        };
        let logger = ActionLogger.getLogger(config.getSessionId());
        logger.log("started a new game session with seed " + instance.sessionConfig.getSeed());
        if (instance.sessionConfig.isDebug()) {
            logger.log("debug mode enabled");
        }
        instance.attackManager = new AttackManager(instance.sessionConfig.getSeed());
        instance.redHerringManager = RedHerringManager.build(5, 2);
        instance.attackManager.injectAttack(instance.storage, CONSTANTS.INFECTED_SPREAD, instance.round);
        instance.attackManager.injectAttack(instance.storage, CONSTANTS.INFECTED_DIRECTED, instance.round);

        instance.attackManagerWorstCase = new AttackManager(instance.sessionConfig.getSeed());
        instance.attackManagerWorstCase.injectAttack(instance.storageWorstCase, CONSTANTS.INFECTED_SPREAD, instance.round);
        instance.attackManagerWorstCase.injectAttack(instance.storageWorstCase, CONSTANTS.INFECTED_DIRECTED, instance.round);

        for (let role of CONSTANTS.ALL_ROLES) {
            instance.redHerringManager.injectRedHerrings(instance.storage, instance.sessionConfig, role, 1, instance.gameInProgress());
        }

        for (let rred of CONSTANTS.ALL_ROLES) {
            instance.redHerringManager.coordinateRedHerrings(instance.storage, rred, instance.gameInProgress());
        }
        logger.logRoundBegins(instance, instance.storage, instance.storageWorstCase, instance.redHerringManager);
        
        instance.notificationManager = await NotificationManager.build(CONSTANTS.NOTIFICATIONS_TSV_FILE);
        instance.notificationManager.updateAllNotifications(instance.storage, instance.round);
        await instance.broadcastUpdate("buildSession", JSON.stringify(instance));
        return instance;
    }

    /**
     * Set round start time to current value and save game data
     */
    startGame() {
        this.roundStartTimeStamp = this.getTimestampStart();
        this.started = true;
        this.broadcastUpdate("startGame", { roundStartTimeStamp: this.roundStartTimeStamp, started: this.started });
    }

    /**
     * Force the game to start even if not all players are ready
     */
    forceGameStart() {
        for (let role of Object.values(this.roles)) {
            role.ready = true;
        }
        this.startGame();
    }

    /**
     * get the amount of time elapsed thus far since round start time
     * @return {number} elapsed time in ms
     */
    getRoundElapsedTime() {
        return this.getTimestampStart() - this.roundStartTimeStamp; // might need some additional logic
    }

    /**
     * set a role's status to ready and check if game can start. If it can start, start it
     * @param roleName 
     */
    makeRoleReady(roleName) {
        try {
            let role = this.roles[roleName];
            role.ready = !role.ready;
            let allReady = true;
            for (let role of Object.values(this.roles)) {
                if (!role.ready) {
                    allReady = false;
                }
            }

            if (allReady) {
                this.startGame();
                return;
            }
        } catch (e) {
            throw new InvalidUsage("invalid role: choose from [scada, pio, it, soc]");
        }
        this.broadcastUpdate("makeRoleReady", { roleName: roleName, isReady: this.roles[roleName].ready });
    }

    /** returns one of the four roles specified by the roleName parameter 
     * Adapter function between two different ways of writing the role names
     * @return {string} roleName
     */
    role(roleName) {
        try {
            return this.roles[roleName];
        } catch (e) {
            throw new InvalidUsage("invalid role: choose from [scada, pio, it, soc]");
        }
    }

    /**
     * start break
     * 
     */
    startBreak() {
        this.inBreak = true;
        this.broadcastUpdate("startBreak", { inBreak: this.inBreak });
    }

    /** advances to the next round, spreading viruses and replenishing available moves
     *  TODO add a mutex around this function so we don't get weird race conditions if app processes a request during a round update (Note: transfered from the python version)
     * @return {string|number} error message | round number
     */
    beginNextRound() {
        if (!this.gameInProgress()) { return "did not advance" }
        let logger = ActionLogger.getLogger()
        logger.log('auto-advancing to round ' + (this.round + 1));
        this.storage.savePercentNodesCaught(this.numNodesCaught, this.round);
        this.round++;
        this.roundStartTimeStamp = this.getTimestampStart();
        this.attackManager.advanceSpreadAttacks(this.round, this.storage);
        this.attackManager.advanceDirectedAttacks(this.round, this.storage);
        this.attackManagerWorstCase.advanceSpreadAttacks(this.round, this.storageWorstCase);
        this.attackManagerWorstCase.advanceDirectedAttacks(this.round, this.storageWorstCase);
        if (!this.gameInProgress()) {
            this.notificationManager.updateAllNotifications(this.storage, this.round);
            this.round = -1
            logger.logEndGame(this);
            let update = {
                percentNodesCaught: this.storage.percentNodesCaught,
                round: this.round,
                roundStartTimeStamp: this.roundStartTimeStamp,
                attackManager: this.attackManager,
                attackManagerWorstCase: this.attackManagerWorstCase,
                allInfectedNodes: this.storage.getAllInfectedNodes(),
                nodes: this.storage.graph.nodes,
                end: true,
            }
            this.broadcastUpdate("advanceRound", update);
            // this.broadcastUpdate("advanceRound", JSON.stringify(this));
            return "end"; // don't spread attacks and red herrings after logging the final round
        }
        for (let role of Object.values(this.roles)) {
            role.resetActions();
        }

        let roundAttacks = this.sessionConfig.getRoundAttacks()
        let numAttacks = roundAttacks[this.round]
        for (let i = 0; i < numAttacks; i++) {
            this.attackManager.injectAttack(this.storage, CONSTANTS.INFECTED_SPREAD, this.round);
            this.attackManagerWorstCase.injectAttack(this.storageWorstCase, CONSTANTS.INFECTED_SPREAD, this.round)
        }

        for (let role of CONSTANTS.ALL_ROLES) {
            this.redHerringManager.injectRedHerrings(this.storage, this.sessionConfig, role, this.round, this.gameInProgress());
        }

        for (let rred of CONSTANTS.ALL_ROLES) {
            this.redHerringManager.coordinateRedHerrings(this.storage, rred, this.gameInProgress());
        }

        logger.logRoundBegins(this, this.storage, this.storageWorstCase, this.redHerringManager);
        this.notificationManager.updateAllNotifications(this.storage, this.round);
        this.inBreak = false;
        let update = {
            percentNodesCaught: this.storage.percentNodesCaught,
            round: this.round,
            roundStartTimeStamp: this.roundStartTimeStamp,
            attackManager: this.attackManager,
            attackManagerWorstCase: this.attackManagerWorstCase,
            allInfectedNodes: this.storage.getAllInfectedNodes(),
            inBreak: this.inBreak,
            nodes: this.storage.graph.nodes,
        }
        this.broadcastUpdate("advanceRound", update);
        //this.broadcastUpdate("advanceRound", JSON.stringify(this));
        return this.round;
    }

    /** get the end game report
     * @return end game report
     */
    getFinalReport() {
        let endGameReport = this.getEndGameReport(this.storage).asArray();
        endGameReport['worst_case'] = 3555; // supposedly a "temporary fix"
        return endGameReport;
    }

    /**
     * calls the build function on StatusReport and returns a StatusReport object
     * This function can be mocked in testing in order to isolate functionality
     * @param {Storage} storage storage
     * @return {StatusReport} statusReport
     */
    getEndGameReport(storage) {
        return StatusReport.build(storage, this.shutoffNodes, this.disconnectedEdges, storage.getAllInfectedNodes(), this.notifyImpact, false);
    }

    /** During the game, players do not necessarily know about all infected nodes so the estimated cost only includes
     * what they know about. After the game, the true total cost is returned
     * Goodwill cost is now 1-10 **TODO update formula (to reflect percent impact?)
     * @return {number} totalCost
     */
    calculateLossIncurred() {
        return StatusReport.build(this.storage, this.shutoffNodes, this.disconnectedEdges, this.storage.getAllInfectedNodes(), this.notifyImpact, this.gameInProgress()).getTotalCost();
    }

    /** checks if the round is 2/3 and is still in the break phase at the beginning
     * @return {bool}
     */
    isInBreak() {
        return this.inBreak;
    }

    /**
     * checks if the game is in progress
     * @return {bool}
     */
    gameInProgress() {
        return this.sessionConfig.getTotalRounds() >= this.round && this.round > 0;
    }

    /** get start timestamps
     */
    getTimestampStart() {
        return Date.now();
    }

    jsonDeserialize(jsonString) {
        let data = JSON.parse(jsonString);
        this.sessionConfig = SessionConfig.jsonDeserialize(data.session_config);
        this.round = data.round;
        this.roundStartTimeStamp = data.round_start_time_stamp;
        this.notifyImpact = data.notify_impact;
        this.totalInfectedCost = data.total_infected_cost;
        this.totalShutoffCost = data.total_shutoff_cost;
        this.storage = Storage.jsonDeserialize(data.storage);
        this.storageWorstCase = Storage.jsonDeserialize(data.storage_worst_case);
        this.started = data.started;
        let roles = data.roles;
        this.roles = {
            [CONSTANTS.PIO]: PIO.newFromData(roles[CONSTANTS.PIO]),
            [CONSTANTS.IT]: IT.newFromData(roles[CONSTANTS.IT]),
            [CONSTANTS.SOC]: SOC.newFromData(roles[CONSTANTS.SOC]),
            [CONSTANTS.SCADA]: SCADA.newFromData(roles[CONSTANTS.SCADA]),
        };
        this.attackManager = AttackManager.jsonDeserialize(data.attack_manager);
        this.attackManagerWorstCase = AttackManager.jsonDeserialize(data.attack_manager_worst_case);
        this.notificationManager = NotificationManager.jsonDeserialize(data.notification_manager);
        this.numNodesCaught = data.num_nodes_caught;
        this.shutoffNodes = data.shutoff_nodes;
        this.disconnectedEdges = data.disconnected_edges;
        this.redHerringManager = RedHerringManager.jsonDeserialize(data.red_herring_manager);
        ActionLogger.jsonDeserialize(data.action_logger);
        this.inBreak = data.in_break;
    }

    toJSON() {
        return {
            session_config: this.sessionConfig,
            round: this.round,
            round_start_time_stamp: this.roundStartTimeStamp,
            notify_impact: this.notifyImpact,
            total_infected_cost: this.totalInfectedCost,
            total_shutoff_cost: this.totalShutoffCost,
            storage: this.storage,
            storage_worst_case: this.storageWorstCase,
            started: this.started,
            roles: this.roles,
            attack_manager: this.attackManager,
            attack_manager_worst_case: this.attackManagerWorstCase,
            notification_manager: this.notificationManager,
            num_nodes_caught: this.numNodesCaught,
            shutoff_nodes: this.shutoffNodes,
            disconnected_edges: this.disconnectedEdges,
            action_logger: ActionLogger.getLogger(),
            red_herring_manager: this.redHerringManager,
            in_break: this.inBreak,
        };
    }

    /**
     * get view for role
     * @return array of data
     */
    getRoleView(role) {
        return this.roles[role].getView(this.storage, this.gameInProgress());
    }

    /**
     * process notify actions
     */
    evaluateNotifyAction(nodeName, role) {
        this.verifyCanMove(role);
        let possibleNodes = this.storage.getNodesByStatus(CONSTANTS.COMPROMISED);
        possibleNodes.concat(this.storage.getNodesByStatus(CONSTANTS.OFF)); //TODO: check this
        // if node is currently infected or shut off notify will have positive impact
        let isAccurate = possibleNodes.includes(nodeName);
        this.processNotification(nodeName, isAccurate);
        this.roles[role].useAction();
        let logger = ActionLogger.getLogger();
        logger.action(role + ' notified about ' + nodeName);
        this.broadcastUpdate("notify", { notifyImpact: this.notifyImpact, role: role, actionsLeft: this.roles[role].actionsLeftThisRound });
    }

    /**
     * calculate cost impact of processing notification impact
     */
    processNotification(nodeName, isAccurate) {
        let node = this.storage.getNodeData(nodeName);
        if (isAccurate) {
            this.notifyImpact += (node.getCostGoodwill() / 20) * node.getCostInfected();
        }
        else {
            this.notifyImpact += (node.getCostGoodwill() / 200) * node.getCostInfected();
        }
    }
    /**
     * check if a player has remaining moves
     * @throws {InvalidUsage} if the player cannot move
     */
    verifyCanMove(role) {
        if (this.round > this.sessionConfig.getTotalRounds()) throw new InvalidUsage("the game has ended");
        if (this.isInBreak()) throw new InvalidUsage("cannot use actions during the break");
        if (!this.roles[role].canUseAction()) throw new InvalidUsage("already used all actions for this round");
    }

    /**
     * allows IT or SCADA to shutoff nodes
     * @param {string} role
     * @param {string} nodeName
     * @throws {InvalidUsage} when incorrect role is used
     * @return array newNotifications
     */
    shutoffNode(role, nodeName) {
        if (![CONSTANTS.IT, CONSTANTS.SCADA].includes(role)) {
            throw new InvalidUsage("only IT and SCADA can shutoff nodes");
        }
        this.verifyCanMove(role);
        let node = this.storage.getNodeData(nodeName);
        let caught = false;
        if (node.isInfected() && !node.isCaught()) {
            ++this.numNodesCaught;
            node.catchNode();
            caught = true;
        }
        node = this.storage.updateNodeStatus(nodeName, CONSTANTS.ALL_ROLES, CONSTANTS.OFF, this.round);
        this.roles[role].useAction();
        let newNotifications = this.notificationManager.addOffNotificationToNode(this.storage, nodeName, this.round);
        let logger = ActionLogger.getLogger();
        logger.action(role + ' shut off ' + nodeName + ', status = ' + (node.isInfected() ? 'infected' : 'not infected'));
        this.shutoffNodes.push(nodeName);
        this.broadcastUpdate("shutoff", { node: Node.build(node), caught: caught, role: role, actionsLeft: this.roles[role].actionsLeftThisRound });
        return newNotifications;
    }

    /**
     * allows PIO to isolate nodes
     * @param {string} role
     * @param {string} nodeName
     * @throws {InvalidUsage} when incorrect role is used
     */
    isolateNode(role, nodeName) {
        if (role !== CONSTANTS.SOC) throw new InvalidUsage("only the SOC can isolate nodes");
        this.verifyCanMove(role);
        let allEdges = this.storage.getNodeEdges(nodeName);
        let node = this.storage.getNodeData(nodeName);
        let caught = false;
        if (node.isInfected() && !node.isCaught()) {
            ++this.numNodesCaught;
            node.catchNode();
            caught = true;
        }
        let logger = ActionLogger.getLogger();
        for (let edge of allEdges) {
            this.storage.disconnectEdge(edge.getSource(), edge.getDestination());
            this.disconnectedEdges.push(edge.getSource() + "-" + edge.getDestination());
            this.notificationManager.addEdgeDisconnectionNotificationToNodes(this.storage, edge.getSource(), edge.getDestination(), this.round);
            logger.action(role + ' disconnected ' + edge.getSource() + ' and ' + edge.getDestination());
        }
        this.roles[role].useAction();
        logger.action(role + ' isolated ' + nodeName + ', status = ' + (node.isInfected() ? 'infected' : 'not infected'));
        this.broadcastUpdate("isolate", { node: Node.build(node), caught: caught, role: role, actionsLeft: this.roles[role].actionsLeftThisRound });
    }

    /**
     * investigate a node and make it's true status visible
     * @param {string} role
     * @param {string} node_name
     * @return visibleStatus 
     */
    investigateNode(role, nodeName) {
        this.verifyCanMove(role);
        let node = this.storage.investigateNode(nodeName);
        this.roles[role].useAction();
        let visibleStatus = node.getVisibleStatus(role, this.gameInProgress());
        this.notificationManager.addNotificationToNode(nodeName, this.round, this.storage);
        let logger = ActionLogger.getLogger();
        logger.action('*' + role + '* investigated ' + nodeName + ', status = ' + visibleStatus);
        this.broadcastUpdate("investigate", { node: Node.build(node), role: role, actionsLeft: this.roles[role].actionsLeftThisRound });
        return visibleStatus
    }

    /** commit data to the database
     */
    async broadcastUpdate(actionType, data) {
        await axios.post('/api/cirt/updateCirt', {
            params: {
                groupId: this.sessionConfig.getSessionId(),
                actionType: actionType,
                data: data
            }
        })
        await axios.post('/api/cirt/saveToDb', {
            params: {
                groupId: this.sessionConfig.getSessionId(),
                session: this
            }
        })
    }

    getStorage() {
        return this.storage;
    }

    getStorageWorstCase() {
        return this.storageWorstCase;
    }

    getRound() {
        return this.round;
    }

    getRoundStartTimeStamp() {
        return this.roundStartTimeStamp;
    }

    getAdditionalInfo() {
        return this.storage.getAdditionalInfo(this);
    }

    getRoles() {
        return this.roles;
    }

    getConfig() {
        return this.sessionConfig;
    }

    getNumNodesCaught() {
        return this.numNodesCaught;
    }

    isStarted() {
        return this.started;
    }

    getRedHerringManager() {
        return this.redHerringManager;
    }
}